import { useRef, useState, type FormEvent, type ReactNode } from 'react';

interface SectionFrameProps {
  title: string;
  children: ReactNode;
  /** Runs when Save and Continue is pressed. */
  onSubmit: () => void | Promise<void>;
  /** Go Back; undefined disables the button (first section). */
  onBack?: (() => void) | undefined;
  /**
   * Clears ONLY the fields the employee edits on this screen. Omit for read-only sections: there is nothing of the
   * employee's own to clear, and employer-owned data must never be cleared from here.
   */
  onClearDraft?: (() => void) | undefined;
  clearDraftDescription?: string;
  busy?: boolean;
  /** A blocking message from the last attempt (save failure or a rule the server enforces). */
  error?: string | null;
  /** Extra, non-error status (for example a saved confirmation). */
  status?: string | null;
  /** Rendered between the content and the footer buttons (for example the section-level blocker). */
  footerNote?: ReactNode;
}

/** The white content panel with the section title and the footer: Clear Draft, Go Back, Save and Continue. There is no Save and Exit. */
export function SectionFrame({
  title,
  children,
  onSubmit,
  onBack,
  onClearDraft,
  clearDraftDescription = 'This clears the fields on this screen. Anything you already saved stays saved until you save again.',
  busy = false,
  error,
  status,
  footerNote,
}: SectionFrameProps) {
  const [confirming, setConfirming] = useState(false);
  const clearTrigger = useRef<HTMLButtonElement>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!busy) void onSubmit();
  }

  function closeConfirm() {
    setConfirming(false);
    clearTrigger.current?.focus();
  }

  return (
    <form className="eo-panel" onSubmit={handleSubmit} noValidate aria-labelledby="eo-section-title">
      <h2 id="eo-section-title" className="eo-panel__title">
        {title}
      </h2>
      <div className="eo-panel__content">{children}</div>
      {error && (
        <p className="eo-banner eo-banner--error" role="alert" data-testid="section-error">
          {error}
        </p>
      )}
      {status && (
        <p className="eo-banner eo-banner--info" role="status">
          {status}
        </p>
      )}
      {footerNote}
      <div className="eo-footer">
        {onClearDraft ? (
          <button ref={clearTrigger} type="button" className="eo-btn eo-btn--danger" onClick={() => setConfirming(true)} disabled={busy}>
            Clear Draft
          </button>
        ) : (
          <span />
        )}
        <div className="eo-footer__right">
          <button type="button" className="eo-btn eo-btn--outline" onClick={onBack} disabled={!onBack || busy}>
            Go Back
          </button>
          <button type="submit" className="eo-btn eo-btn--primary" disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </div>

      {confirming && onClearDraft && (
        <div className="eo-dialog-backdrop">
          <div
            className="eo-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="eo-clear-title"
            aria-describedby="eo-clear-desc"
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeConfirm();
            }}
          >
            <h3 id="eo-clear-title">Clear this draft?</h3>
            <p id="eo-clear-desc">{clearDraftDescription}</p>
            <div className="eo-dialog__actions">
              <button type="button" className="eo-btn eo-btn--outline" onClick={closeConfirm} autoFocus>
                Cancel
              </button>
              <button
                type="button"
                className="eo-btn eo-btn--danger"
                onClick={() => {
                  onClearDraft();
                  closeConfirm();
                }}
              >
                Yes, clear it
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
